/**
 * Sidebar - Navigation Component
 *
 * Left sidebar with icon + label navigation.
 * Supports collapsed mode (icons only) and expanded mode (icons + labels).
 */

import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
} from '@mui/icons-material';

import { SIDEBAR_ORDER, WORKSPACES, SIDEBAR_WIDTH } from './constants';

/**
 * Single navigation item
 */
const NavItem = ({
  workspace,
  isActive,
  expanded,
  onClick,
  colors,
}) => {
  const Icon = workspace.icon;

  // Extract color values (handle nested theme structure)
  const successColor = colors.success?.main || colors.success || '#22c55e';
  const textPrimary = colors.text?.primary || colors.textPrimary || '#ffffff';
  const textSecondary = colors.text?.secondary || colors.textSecondary || '#a1a1aa';

  const content = (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: expanded ? 2 : 0,
        py: 1.25,
        mx: expanded ? 1 : 0.5,
        my: 0.25,
        cursor: 'pointer',
        borderRadius: 1,
        background: isActive
          ? `linear-gradient(90deg, ${successColor}30 0%, ${successColor}10 100%)`
          : 'transparent',
        borderLeft: isActive ? `4px solid ${successColor}` : '4px solid transparent',
        color: isActive ? textPrimary : textSecondary,
        justifyContent: expanded ? 'flex-start' : 'center',
        transition: 'all 0.2s ease',
        position: 'relative',
        boxShadow: isActive ? `inset 0 0 20px ${successColor}15` : 'none',
        '&:hover': {
          bgcolor: isActive ? `${successColor}35` : colors.surfaceHover,
          color: textPrimary,
          transform: 'translateX(2px)',
        },
        // Animated glow effect for active item
        ...(isActive && {
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            background: successColor,
            boxShadow: `0 0 12px ${successColor}, 0 0 24px ${successColor}60`,
            animation: 'pulse 2s ease-in-out infinite',
          },
          '@keyframes pulse': {
            '0%, 100%': {
              boxShadow: `0 0 8px ${successColor}, 0 0 16px ${successColor}40`,
            },
            '50%': {
              boxShadow: `0 0 16px ${successColor}, 0 0 32px ${successColor}60`,
            },
          },
        }),
      }}
    >
      {/* Icon with active indicator */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Icon
          sx={{
            fontSize: 22,
            color: isActive ? successColor : 'inherit',
            filter: isActive ? `drop-shadow(0 0 6px ${successColor})` : 'none',
            transition: 'all 0.2s ease',
            transform: isActive ? 'scale(1.1)' : 'scale(1)',
          }}
        />
        {/* Active dot indicator */}
        {isActive && (
          <Box
            sx={{
              position: 'absolute',
              top: -3,
              right: -5,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: successColor,
              boxShadow: `0 0 8px ${successColor}, 0 0 12px ${successColor}`,
              animation: 'dotPulse 1.5s ease-in-out infinite',
              '@keyframes dotPulse': {
                '0%, 100%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '50%': {
                  transform: 'scale(1.2)',
                  opacity: 0.8,
                },
              },
            }}
          />
        )}
      </Box>
      {expanded && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: isActive ? 700 : 400,
            fontSize: '0.85rem',
            whiteSpace: 'nowrap',
            color: isActive ? successColor : 'inherit',
            textShadow: isActive ? `0 0 10px ${successColor}50` : 'none',
            letterSpacing: isActive ? '0.02em' : 'normal',
          }}
        >
          {workspace.label}
        </Typography>
      )}
    </Box>
  );

  // Show tooltip only when collapsed
  if (!expanded) {
    return (
      <Tooltip title={workspace.label} placement="right" arrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

/**
 * Sidebar Component
 */
const Sidebar = ({
  colors,
  expanded,
  onToggle,
  activeWorkspaceId,
  onWorkspaceChange,
  accessibleWorkspaces,
}) => {
  // Get accessible workspace IDs for filtering
  const accessibleIds = new Set(accessibleWorkspaces.map(ws => ws.id));

  // Filter sidebar items by accessibility
  const visibleItems = SIDEBAR_ORDER.filter(id => accessibleIds.has(id));

  // Separate home from other items
  const homeItem = visibleItems.find(id => id === 'home');
  const otherItems = visibleItems.filter(id => id !== 'home');

  const sidebarWidth = expanded ? SIDEBAR_WIDTH.expanded : SIDEBAR_WIDTH.collapsed;

  // Extract color values (handle nested theme structure)
  const borderColor = colors.border?.default || colors.border || '#27272a';
  const textPrimary = colors.text?.primary || colors.textPrimary || '#ffffff';
  const textMuted = colors.text?.muted || colors.textMuted || '#71717a';

  return (
    <Box
      sx={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.surface,
        borderRight: `1px solid ${borderColor}`,
        transition: 'width 0.2s ease, min-width 0.2s ease',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'space-between' : 'center',
          px: expanded ? 2 : 1,
          py: 1.5,
          borderBottom: `1px solid ${borderColor}`,
          minHeight: 48,
        }}
      >
        {expanded && (
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: textPrimary,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
            }}
          >
            FAST Studio
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={onToggle}
          sx={{
            color: textMuted,
            '&:hover': { color: textPrimary, bgcolor: colors.surfaceHover },
          }}
        >
          {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Navigation Items */}
      {/* pt gives clearance below the header's collapse button so its larger invisible
          hit/ripple area doesn't overlap the scrollbar thumb at the very top of this list —
          without it, a drag started near the top of the scrollbar can trigger onToggle instead. */}
      <Box sx={{ flex: 1, pt: 2, pb: 1, overflow: 'auto', scrollbarGutter: 'stable' }}>
        {/* Home Item */}
        {homeItem && (
          <>
            <NavItem
              workspace={WORKSPACES[homeItem]}
              isActive={activeWorkspaceId === homeItem}
              expanded={expanded}
              onClick={() => onWorkspaceChange(homeItem)}
              colors={colors}
            />
            <Divider sx={{ my: 1, mx: expanded ? 2 : 1, borderColor: borderColor }} />
          </>
        )}

        {/* Other Navigation Items */}
        {otherItems.map(id => (
          <NavItem
            key={id}
            workspace={WORKSPACES[id]}
            isActive={activeWorkspaceId === id}
            expanded={expanded}
            onClick={() => onWorkspaceChange(id)}
            colors={colors}
          />
        ))}
      </Box>

      {/* Footer - Version/Info */}
      {expanded && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: `1px solid ${borderColor}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: textMuted,
              fontSize: '0.65rem',
            }}
          >
            v1.0.0
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Sidebar;
